/**
 * The tags a surface's rules can test, for the field picker: hours — the
 * enabled Tag Rules › Hours tags (`hour-profile/tags`); texts, gateways,
 * lists — the enabled Tag Rules › Texts / Gateways / Lists tags (lists add
 * the static tags and New / Unused) (`rule-tags/:kind`, Phase 25). Read with
 * the Drop Generator's own view permission so every editor can pick them.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useQuery } from '@tanstack/react-query';
import type { DropGenTagOption } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../../useDropGenerator';
import type { RuleSurface } from '../rule-surfaces';

/** Phase 39 — `drops`: Tag Rules › Drops and the static tags, for a verdict on today's drops. */
export type TagCatalogSurface = RuleSurface | 'drops';

const PATH: Record<TagCatalogSurface, string> = {
  hours: '/drop-generator/hour-profile/tags',
  texts: '/drop-generator/rule-tags/texts',
  gateways: '/drop-generator/rule-tags/gateways',
  lists: '/drop-generator/rule-tags/lists',
  drops: '/drop-generator/rule-tags/drops',
  routeTest: '/drop-generator/rule-tags/gateways',
};

export function useRuleTagCatalog(surface: TagCatalogSurface, enabled = true) {
  return useQuery({
    queryKey: [...dropGeneratorKeys.all, 'rule-tags', surface] as const,
    queryFn: async () => {
      const res = await api.get<{ data: DropGenTagOption[] }>(PATH[surface]);
      return res.data.data ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
}

/** Hours' own hook (kept for its callers). */
export const useHourTagCatalog = (enabled = true) => useRuleTagCatalog('hours', enabled);
